// ============================================================================
// CHANGO V5.0 — Register Page (§9.6.2a 阶段 1：极简，无营业执照)
// ============================================================================
// 7 必填项（全部红 `*`）：邮箱 + 邮箱验证码 + 手机号 + 密码 + 公司名 + 持有人姓名 + 协议同意
//   - 邮箱验证码：点"获取验证码" → POST /api/auth/email-code/send → 用户输入 → 提交时一并送
//   - 失焦未填显示红色边框 + 下方红字"该项必填"
//   - 协议同意：必须勾选才能提交（§9.6.2b 不可跳过）
//   - 注册后跳 `/app/welcome`（不再跳 `/app/dashboard`）
//
// react-hook-form + zod for validation. Submission is disabled until every
// required field is populated AND the "我已阅读并同意" tick is set.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { api, HttpError } from '../../api/client.js';
import { useAuth } from '../../hooks/useAuth.js';
import AgreementModal from '../../components/AgreementModal.jsx';

const schema = z.object({
  email: z.string().trim().email('请输入有效的邮箱'),
  email_code: z.string().regex(/^\d{6}$/, '邮箱验证码是 6 位数字'),
  phone: z.string().regex(/^1[3-9]\d{9}$/, '请输入中国大陆手机号'),
  password: z.string()
    .min(8, '至少 8 位')
    .regex(/[a-z]/, '需包含小写字母')
    .regex(/[A-Z]/, '需包含大写字母')
    .regex(/\d/, '需包含数字'),
  password_confirm: z.string(),
  company_name: z.string().trim().min(2, '请填写公司全称').max(200),
  account_holder_name: z.string().trim().min(2, '请填写姓名').max(50),
  agreed: z.literal(true, {
    errorMap: () => ({ message: '请先勾选并同意协议' })
  })
}).refine((v) => v.password === v.password_confirm, {
  message: '两次输入的密码不一致',
  path: ['password_confirm']
});

export default function Register() {
  const { register: doRegister } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [versions, setVersions] = useState({ tos_version: null, privacy_version: null });
  const [sendingEmailCode, setSendingEmailCode] = useState(false);
  const [emailCodeCountdown, setEmailCodeCountdown] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      email_code: '',
      phone: '',
      password: '',
      password_confirm: '',
      company_name: '',
      account_holder_name: '',
      agreed: false
    }
  });

  const agreedChecked = watch('agreed');
  const emailValue = watch('email');

  // 邮箱验证码倒计时
  useEffect(() => {
    if (emailCodeCountdown <= 0) return;
    const timer = setTimeout(() => setEmailCodeCountdown(emailCodeCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [emailCodeCountdown]);

  // 点击"获取验证码"
  const handleSendEmailCode = async () => {
    if (!emailValue || !emailValue.includes('@')) {
      setServerError('请先填写有效的邮箱');
      return;
    }
    setSendingEmailCode(true);
    setServerError(null);
    try {
      await api.post('/api/auth/email-code/send', { email: emailValue });
      setEmailCodeCountdown(60);
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : '发送失败，请重试';
      setServerError(msg);
    } finally {
      setSendingEmailCode(false);
    }
  };

  const onSubmit = async (values) => {
    setServerError(null);
    setSubmitting(true);
    try {
      await doRegister({
        email: values.email,
        email_code: values.email_code,
        phone: values.phone,
        password: values.password,
        company_name: values.company_name,
        account_holder_name: values.account_holder_name,
        agreed_tos_version: versions.tos_version,
        agreed_privacy_version: versions.privacy_version
      });
      // 注册成功后直接跳 /app/welcome（不再跳 /verify）
      navigate('/app/welcome');
    } catch (err) {
      if (err instanceof HttpError) {
        if (err.code === 'EMAIL_EXISTS' || err.code === 'EMAIL_TAKEN') setServerError('该邮箱已注册');
        else if (err.code === 'AGREEMENT_VERSION_MISMATCH') setServerError('协议已更新，请重新勾选');
        else setServerError(err.message || '注册失败');
      } else {
        setServerError(err?.message || '网络错误，请重试');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onAcceptAgreements = ({ tos_version, privacy_version }) => {
    setVersions({ tos_version, privacy_version });
    setValue('agreed', true, { shouldValidate: true });
    setModalOpen(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12">
      <div className="mx-auto w-full max-w-2xl px-4">
        <h1 className="mb-2 text-2xl font-semibold text-slate-800">注册新工作区</h1>
        <p className="mb-8 text-sm text-slate-500">
          工作区创建后，您将是默认的 boss 角色，可再邀请财务与销售成员。
        </p>

        {serverError ? (
          <div role="alert" className="alert-error mb-6">{serverError}</div>
        ) : null}

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="card space-y-6 p-8">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {/* 邮箱 + 邮箱验证码 */}
            <div className="md:col-span-2">
              <label className="label" htmlFor="email">
                <span className="text-red-600">*</span> 登录邮箱
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                className={`field ${errors.email ? 'field-error' : ''}`}
                {...register('email')}
              />
              {errors.email ? <p className="err">{errors.email.message}</p> : null}
            </div>

            <div className="md:col-span-2">
              <label className="label" htmlFor="email_code">
                <span className="text-red-600">*</span> 邮箱验证码
              </label>
              <div className="flex gap-2">
                <input
                  id="email_code"
                  type="text"
                  placeholder="6 位数字"
                  maxLength="6"
                  className={`flex-1 field ${errors.email_code ? 'field-error' : ''}`}
                  {...register('email_code')}
                />
                <button
                  type="button"
                  onClick={handleSendEmailCode}
                  disabled={emailCodeCountdown > 0 || sendingEmailCode || !emailValue || !emailValue.includes('@')}
                  className="btn btn-outline whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {emailCodeCountdown > 0 ? `重试 (${emailCodeCountdown}s)` : sendingEmailCode ? '发送中…' : '获取验证码'}
                </button>
              </div>
              {errors.email_code ? <p className="err">{errors.email_code.message}</p> : null}
            </div>

            {/* 手机号 */}
            <div className="md:col-span-2">
              <label className="label" htmlFor="phone">
                <span className="text-red-600">*</span> 手机号
              </label>
              <input
                id="phone"
                type="tel"
                autoComplete="tel"
                className={`field ${errors.phone ? 'field-error' : ''}`}
                {...register('phone')}
              />
              {errors.phone ? <p className="err">{errors.phone.message}</p> : null}
            </div>

            {/* 密码 */}
            <div>
              <label className="label" htmlFor="password">
                <span className="text-red-600">*</span> 密码
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                className={`field ${errors.password ? 'field-error' : ''}`}
                {...register('password')}
              />
              {errors.password ? <p className="err">{errors.password.message}</p> : <p className="hint">至少 8 位，含大小写字母和数字</p>}
            </div>

            <div>
              <label className="label" htmlFor="password_confirm">
                <span className="text-red-600">*</span> 确认密码
              </label>
              <input
                id="password_confirm"
                type="password"
                autoComplete="new-password"
                className={`field ${errors.password_confirm ? 'field-error' : ''}`}
                {...register('password_confirm')}
              />
              {errors.password_confirm ? <p className="err">{errors.password_confirm.message}</p> : null}
            </div>

            {/* 公司名 */}
            <div className="md:col-span-2">
              <label className="label" htmlFor="company_name">
                <span className="text-red-600">*</span> 公司全称
              </label>
              <input
                id="company_name"
                className={`field ${errors.company_name ? 'field-error' : ''}`}
                {...register('company_name')}
              />
              {errors.company_name ? <p className="err">{errors.company_name.message}</p> : null}
            </div>

            {/* 持有人姓名 */}
            <div className="md:col-span-2">
              <label className="label" htmlFor="account_holder_name">
                <span className="text-red-600">*</span> 持有人姓名
              </label>
              <input
                id="account_holder_name"
                className={`field ${errors.account_holder_name ? 'field-error' : ''}`}
                {...register('account_holder_name')}
              />
              {errors.account_holder_name ? <p className="err">{errors.account_holder_name.message}</p> : null}
            </div>
          </div>

          {/* 协议同意 */}
          <div className="rounded border border-slate-200 bg-slate-50 p-4">
            <label className="inline-flex items-start gap-3 text-sm text-slate-700">
              <input
                id="agreed"
                type="checkbox"
                checked={Boolean(agreedChecked)}
                readOnly
                onClick={(e) => {
                  e.preventDefault();
                  setModalOpen(true);
                }}
                className="mt-1"
              />
              <span>
                <span className="text-red-600">*</span> 我已阅读并同意
                <button type="button" className="text-chango-600 hover:underline" onClick={() => setModalOpen(true)}>
                  《用户服务协议》《隐私政策》
                </button>
              </span>
            </label>
            {errors.agreed ? <p className="err">{errors.agreed.message}</p> : null}
            {agreedChecked && versions.tos_version && versions.privacy_version ? (
              <p className="hint">
                已记录同意版本：{versions.tos_version} / {versions.privacy_version}
              </p>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-4">
            <Link to="/login" className="text-sm text-chango-600 hover:underline">已有账号？返回登录</Link>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? <span className="spinner" aria-hidden="true" /> : null}
              {submitting ? '立即注册中…' : '立即注册'}
            </button>
          </div>
        </form>
      </div>

      <AgreementModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onAccept={onAcceptAgreements}
      />
    </div>
  );
}
